import { useEffect, useMemo, useState } from 'react';
import { Accordion, Box, Card, Icon, Text } from '@nimbus-ds/components';
import { MarketingIcon, RocketIcon, StarIcon } from '@nimbus-ds/icons';
import { DataList } from '@nimbus-ds/patterns';
import {
  OnboardingTasksResponseDto,
  OnboardingTaskUpdateRequestDto,
  PromoteGroupNameType,
} from '@tiendanube/common';
import { SHOW_PROMOTE_TASKS_MOBILE_TAG } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import {
  trackingPromoteTaskClick,
  trackingPromoteTaskSkip,
} from 'App/tracking';
import { IconBox } from 'commons/components';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import { updateOnboardingTaskStatusAction } from 'domains/Dashboard/slices/onBoardingSlice';
import { useNuvemMarketing } from 'domains/Marketing/hook';
import { CompletedPromoteGroupItem } from './components/CompletedPromoteGroupItem';
import { filterPromoteTasks, promoteTaskDigest } from './utils';
import { TaskData } from '../GenericTaskList';
import TaskItem from '../GenericTaskList/TaskItem';
import './PromoteAccordion.scss';

const PROMOTE_GROUP_ICON: Record<PromoteGroupNameType, React.ReactElement> = {
  generate_confidence: <StarIcon />,
  increase_traffic: <MarketingIcon />,
  improve_conversion: <RocketIcon />,
};

interface PromoteAccordionProps {
  tasks: NonNullable<OnboardingTasksResponseDto['promoteGroupedTasks']>;
}

export function PromoteAccordion({ tasks }: Readonly<PromoteAccordionProps>) {
  const dispatch = useAppDispatch();
  const [visibleTasks, setVisibleTasks] = useState(tasks);
  const isMobileDevice = useIsMobileDevice();
  const [hasShowPromoteTasksTag] = useHasTags(SHOW_PROMOTE_TASKS_MOBILE_TAG);
  const { hasAccessToNuvemMarketing } = useNuvemMarketing();

  const selectedDefault = useMemo(() => {
    const index = visibleTasks.findIndex((group) => group.tasks.length > 0);
    return index === -1 ? 0 : index;
  }, [visibleTasks]);

  useEffect(() => {
    setVisibleTasks(
      filterPromoteTasks(
        tasks,
        isMobileDevice && !hasShowPromoteTasksTag,
        hasAccessToNuvemMarketing,
      ),
    );
  }, [
    tasks,
    isMobileDevice,
    hasShowPromoteTasksTag,
    hasAccessToNuvemMarketing,
  ]);

  if (
    !visibleTasks ||
    visibleTasks.length === 0 ||
    visibleTasks.every((group) => group.tasks.length === 0)
  )
    return null;

  const findTaskById = (group: string, id: string) =>
    tasks
      .find((taskGroup) => taskGroup.name === group)
      ?.tasks?.find((task) => task.name === id);

  const hideTask = (groupName: string, taskName: string) => {
    setVisibleTasks((prevTasks) =>
      prevTasks.map((group) => {
        if (group.name === groupName) {
          return {
            ...group,
            tasks: group.tasks.filter((task) => task.name !== taskName),
          };
        }
        return group;
      }),
    );
  };

  const handleAction = (group: string) => async (item: TaskData) => {
    const originalTask = findTaskById(group, item.id);

    if (originalTask) {
      trackingPromoteTaskClick(group, originalTask.name);
    }

    if (originalTask?.onCtaLinkClickComplete) {
      const payload: OnboardingTaskUpdateRequestDto = {
        task: originalTask.name,
        status: 'completed',
      };
      hideTask(group, originalTask.name);
      await dispatch(updateOnboardingTaskStatusAction(payload));
    }
  };

  const handleSkip = (group: string) => async (item: TaskData) => {
    const originalTask = findTaskById(group, item.id);

    if (originalTask) {
      trackingPromoteTaskSkip(group, originalTask.name);
      const payload: OnboardingTaskUpdateRequestDto = {
        task: originalTask.name,
        status: 'skipped',
      };
      hideTask(group, originalTask.name);
      await dispatch(updateOnboardingTaskStatusAction(payload));
    }
  };

  return (
    <div className="stratus--promote-accordion-card">
      <Card padding="none">
        <Box display="flex" flexDirection="column">
          <div className="stratus--promote-accordion">
            <Accordion selectedDefault={`${selectedDefault}`}>
              {visibleTasks.map((group, index) =>
                visibleTasks[index].tasks.length === 0 ? (
                  <CompletedPromoteGroupItem
                    index={index}
                    key={group.name}
                    title={group.title}
                  />
                ) : (
                  <Accordion.Item key={group.title} index={`${index}`}>
                    <Accordion.Header borderTop={index === 0 ? 'none' : 'base'}>
                      <Box width="100%" display="flex" alignItems="center">
                        <Box>
                          <IconBox
                            backgroundColor="primary-surface"
                            color="primary-textLow"
                            borderRadius="2"
                            height="28px"
                            width="28px"
                            marginRight="2"
                          >
                            <Icon
                              source={PROMOTE_GROUP_ICON[group.name]}
                              color="currentColor"
                            />
                          </IconBox>
                        </Box>
                        <Text fontWeight="medium" color="neutral-textHigh">
                          {group.title}
                        </Text>
                      </Box>
                    </Accordion.Header>
                    <Accordion.Body padding="none">
                      <div className="stratus--promote-accordion-tasks">
                        <DataList>
                          {visibleTasks[index].tasks.map((task) => (
                            <TaskItem
                              key={task.name}
                              data={promoteTaskDigest(task)}
                              contentType="promoteGrouped"
                              actionType="promote"
                              onAction={handleAction(visibleTasks[index].name)}
                              onSkip={handleSkip(visibleTasks[index].name)}
                              paddingVertical="2"
                            />
                          ))}
                        </DataList>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ),
              )}
            </Accordion>
          </div>
        </Box>
      </Card>
    </div>
  );
}
